import { createApiClient } from '@bafkal/ui-shared/api';
import { Auth, Hub } from 'aws-amplify';
import { API_ROUTES, HTTP_STATUS, ENV } from '../constants';

// All API traffic is routed through the gateway. There is no per-service base URL.
// In staging/production this is set at build time via the CI/CD pipeline.
// For local development, create apps/publisher-portal/.env.local and set:
//   REACT_APP_ADS_PUBLISHER_API_BASE_URL=http://localhost:8080/v1
if (!process.env.REACT_APP_ADS_PUBLISHER_API_BASE_URL && ENV.IS_DEVELOPMENT) {
  console.warn(
    '[api] REACT_APP_ADS_PUBLISHER_API_BASE_URL is not set. ' +
    'Create .env.local and set it to http://localhost:8080/v1 (local gateway).'
  );
}
const API_BASE_URL = process.env.REACT_APP_ADS_PUBLISHER_API_BASE_URL;

export const apiClient = createApiClient({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to set the authorization token for API requests
export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Attach an Axios request interceptor to ensure each request uses a fresh Cognito access token
apiClient.interceptors.request.use(
  async (config) => {
    // All requests route through the gateway
    config.baseURL = API_BASE_URL;
    
    // Add JWT token for all requests
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
      // Keep the default header in sync for non-interceptor code paths
      setAuthToken(accessToken);
    } catch (err) {
      // No session / not authenticated - leave request without Authorization header
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Helper to centralize sign-out behavior
export const forceSignOut = async (opts = { hostedLogout: false }) => {
  try {
    await Auth.signOut();
  } catch (e) {
    /* ignore */
  }
  try {
    setAuthToken(null);
  } catch (e) {
    /* ignore */
  }

  try {
    if (ENV.IS_DEVELOPMENT) {
      console.warn('[api] forcing sign-out; notifying app');
    }
    Hub.dispatch('app', { event: 'signedOut' });

    if (!opts.hostedLogout) {
      // Fallback: navigate to login to avoid a stuck UI
      try {
        setTimeout(() => {
          if (ENV.IS_DEVELOPMENT) {
            console.warn('[api] fallback navigation to /login');
          }
          window.location.replace('/login');
        }, 50);
      } catch (e) {
        /* ignore on non-browser env */
      }
    }
  } catch (e) {
    /* ignore */
  }
};

// Response interceptor: on 401, attempt a single refresh and retry once
let refreshPromise = null;
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest?._retry) {
      originalRequest._retry = true;

      try {
        // Use a single shared promise so concurrent 401s only trigger one refresh
        if (!refreshPromise) {
          refreshPromise = (async () => {
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken().getJwtToken();
            setAuthToken(accessToken);
            return accessToken;
          })();
        }

        const accessToken = await refreshPromise;
        refreshPromise = null;

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        return apiClient(originalRequest);
      } catch (refreshErr) {
        refreshPromise = null;
        await forceSignOut({ hostedLogout: false });
        return Promise.reject(error);
      }
    }

    // If we already retried once and still got a 401, sign the user out
    if (error.response && error.response.status === HTTP_STATUS.UNAUTHORIZED && originalRequest?._retry) {
      await forceSignOut({ hostedLogout: false });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// ===========================
// API Functions
// ===========================

// Function to fetch ad inventory — page param maps to PageEnvelope contract.
export const fetchAdInventory = async (page = 1) => {
  const response = await apiClient.get(API_ROUTES.INVENTORY.BASE, { params: { page } });
  return response.data;
};

// Function to create a new ad unit
export const createAdUnit = async (adUnitData, idempotencyKey) => {
  const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
  const response = await apiClient.post(API_ROUTES.INVENTORY.AD_UNIT, adUnitData, { headers });
  return response.data;
};

// Function to update an existing ad unit
export const updateAdUnit = async (adUnitId, adUnitData) => {
  const response = await apiClient.put(API_ROUTES.INVENTORY.AD_UNIT_BY_ID(adUnitId), adUnitData);
  return response.data;
};

// Function to delete an ad unit
export const deleteAdUnit = async (adUnitId) => {
  const response = await apiClient.delete(API_ROUTES.INVENTORY.AD_UNIT_BY_ID(adUnitId));
  return response.data;
};

// Function to fetch dashboard data (analytics and performance metrics)
export const fetchDashboardData = async (timeRange = '7d', adUnitId = 'all') => {
  const response = await apiClient.get(API_ROUTES.DASHBOARD, {
    params: { timeRange, adUnitId }
  });
  return response.data;
};

// ===========================
// Publisher Reporting API Functions (RAS)
// ===========================

export const fetchPublisherSummary = async (from, to) => {
  const response = await apiClient.get(API_ROUTES.REPORTING.SUMMARY, {
    params: { from, to }
  });
  return response.data;
};

export const fetchPublisherByAdUnit = async (from, to, page = 1, pageSize = 20, sortBy = 'impressions', sortDir = 'desc') => {
  const response = await apiClient.get(API_ROUTES.REPORTING.BY_AD_UNIT, {
    params: { from, to, page, pageSize, sortBy, sortDir }
  });
  return response.data;
};

export const fetchPublisherByDay = async (from, to, page = 1, pageSize = 90) => {
  const response = await apiClient.get(API_ROUTES.REPORTING.BY_DAY, {
    params: { from, to, page, pageSize }
  });
  return response.data;
};

export const fetchPublisherByFormat = async (from, to, page = 1, pageSize = 20) => {
  const response = await apiClient.get(API_ROUTES.REPORTING.BY_FORMAT, {
    params: { from, to, page, pageSize }
  });
  return response.data;
};

export const fetchPublisherNoFillReasons = async (from, to, page = 1, pageSize = 20) => {
  const response = await apiClient.get(API_ROUTES.REPORTING.NO_FILL_REASONS, {
    params: { from, to, page, pageSize }
  });
  return response.data;
};

export const exportPublisherReport = async (reportType = 'ad-unit', from, to) => {
  const response = await apiClient.get(API_ROUTES.REPORTING.EXPORT, {
    params: { reportType, from, to },
    responseType: 'blob'
  });
  return response.data;
};

// ===========================
// Placement API Functions
// ===========================

export const fetchPlacements = async (page = 1) => {
  const response = await apiClient.get(API_ROUTES.INVENTORY.PLACEMENT, { params: { page } });
  return response.data;
};

export const createPlacement = async (data, idempotencyKey) => {
  const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
  const response = await apiClient.post(API_ROUTES.INVENTORY.PLACEMENT, data, { headers });
  return response.data;
};

export const updatePlacement = async (id, data) => {
  const response = await apiClient.put(API_ROUTES.INVENTORY.PLACEMENT_BY_ID(id), data);
  return response.data;
};

export const deletePlacement = async (id) => {
  const response = await apiClient.delete(API_ROUTES.INVENTORY.PLACEMENT_BY_ID(id));
  return response.data;
};

// ===========================
// Publisher Settings API Functions
// ===========================

export const fetchPublisherSettings = async () => {
  const response = await apiClient.get(API_ROUTES.PUBLISHER_SETTINGS);
  return response.data;
};

export const updatePublisherSettings = async (data, idempotencyKey) => {
  const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
  const response = await apiClient.put(API_ROUTES.PUBLISHER_SETTINGS, data, { headers });
  return response.data;
};

// ===========================
// Publisher API Key Functions
// ===========================

export const fetchPublisherApiKeys = async () => {
  const response = await apiClient.get(API_ROUTES.PUBLISHER_KEYS);
  return response.data;
};

export const createPublisherApiKey = async (name, idempotencyKey) => {
  const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {};
  const response = await apiClient.post(API_ROUTES.PUBLISHER_KEYS, { name }, { headers });
  return response.data;
};

export const revokePublisherApiKey = async (id) => {
  const response = await apiClient.delete(`${API_ROUTES.PUBLISHER_KEYS}/${id}`);
  return response.data;
};